<template>
  <div class="admin-toolbar bg-white border-b border-gray-200 px-6 py-4">
    <!-- Ligne principale -->
    <div class="flex items-center justify-between mb-4">
      <!-- Bouton retour et titre -->
      <div class="flex items-center gap-4">
        <button
          class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          @click="handleBack">
          <ArrowLeftIcon class="w-4 h-4" />
          Retour Dashboard
        </button>
        <h1 class="text-2xl font-bold text-gray-900">{{ title }}</h1>
      </div>

      <!-- Actions principales -->
      <div class="flex items-center gap-3">
        <!-- Import (visible seulement pour les admins et superadmins) -->
        <div v-if="isAdmin() || isSuperAdmin()" class="relative">
          <button
            class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            @click="emit('import', [], 'json')">
            <UploadIcon class="w-4 h-4" />
            Importer
          </button>
        </div>

        <!-- Export -->
        <div ref="exportDropdown" class="relative">
          <button
            class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            @click="toggleExportMenu">
            <DownloadIcon class="w-4 h-4" />
            Exporter
            <ChevronDownIcon class="w-4 h-4" />
          </button>

          <!-- Menu déroulant export -->
          <div v-if="showExportMenu"
            class="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
            <div class="py-1">
              <button class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                @click="handleExport('json')">
                <FileTextIcon class="w-4 h-4" />
                Exporter JSON
              </button>
              <button class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                @click="handleExport('csv')">
                <TableIcon class="w-4 h-4" />
                Exporter CSV
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Ligne de recherche et filtres -->
    <div class="flex items-center gap-4">
      <!-- Barre de recherche -->
      <div class="flex-1 max-w-md">
        <div class="relative">
          <SearchIcon v-if="!searchTerm"
            class="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input v-model="searchTerm" type="text" placeholder="Rechercher..."
            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            @input="handleSearch" />
          <button v-if="searchTerm"
            class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            @click="clearSearch">
            <XIcon class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- Compteur d'éléments -->
      <div class="text-sm text-gray-500">
        {{ filteredCount }} / {{ totalCount }} éléments
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/stores/auth';
import {
  ArrowLeft as ArrowLeftIcon,
  ChevronDown as ChevronDownIcon,
  Download as DownloadIcon,
  FileText as FileTextIcon,
  Search as SearchIcon,
  Table as TableIcon,
  Upload as UploadIcon,
  X as XIcon
} from 'lucide-vue-next';
import { onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

interface AdminToolbarProps {
  title: string;
  backRoute: string;
  totalCount: number;
  filteredCount: number;
  exportFilename: string;
  searchableFields: string[];
}

// Type pour les données d'import
type ImportData = Record<string, unknown>;

interface AdminToolbarEmits {
  search: [searchTerm: string];
  export: [format: 'json' | 'csv'];
  import: [data: ImportData[], format: 'json' | 'csv'];
}

const props = defineProps<AdminToolbarProps>();
const emit = defineEmits<AdminToolbarEmits>();

const router = useRouter();
const authStore = useAuthStore();

// État local
const searchTerm = ref('');
const showExportMenu = ref(false);
const exportDropdown = ref<HTMLElement>();

// Permissions
const isAdmin = (): boolean => authStore.user?.role === 'admin';
const isSuperAdmin = (): boolean => authStore.user?.role === 'superadmin';

// Navigation
const handleBack = (): void => {
  router.push(props.backRoute);
};

// Recherche
const handleSearch = (): void => {
  emit('search', searchTerm.value);
};

const clearSearch = (): void => {
  searchTerm.value = '';
  emit('search', '');
};

// Export
const toggleExportMenu = (): void => {
  showExportMenu.value = !showExportMenu.value;
};

const handleExport = (format: 'json' | 'csv'): void => {
  emit('export', format);
  showExportMenu.value = false;
};

// Fermer le menu export en cliquant ailleurs
const handleClickOutside = (event: Event): void => {
  if (exportDropdown.value && !exportDropdown.value.contains(event.target as Node)) {
    showExportMenu.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Styles spécifiques si nécessaire */
</style>